<template>
    <div>
        <div class="row mb-10" style="align-items: flex-end;">
            <div :class="{ 'col-7-md' : !large, 'col-8-md' : large }">
                <input-text
                    input-type="text"
                    :name="fieldNames.postcode"
                    v-model="address.postcode"
                    label="Postcode"
                    placeholder="Postcode"
                    :small="!large"
                    :validation-state="validationState.postcode"
                    @blur="getStreets(false)"
                ></input-text>
            </div>
            <div :class="{ 'col-5-md' : !large, 'col-4-md' : large }">
                <button class="button button--ghost button--full mb-20" type="button"
                        :class="{ 'button--small' : ! large}"
                        :disabled="loadingStreet || disableLookup"
                        @click="getStreets(false)">
                    Lookup
                </button>
            </div>
        </div>

        <div v-if="streets.length > 0">
            <div v-show="!manual">
                <drop-down
                    v-model="houseId"
                    name="houseId"
                    :options="streetOptions"
                    label="Address"
                    :small="!large"
                    :loading="loadingAddress"
                    @change="getAddress"
                ></drop-down>
            </div>

            <p class="mb-10">
                <a href="#" @click.prevent.stop="toggleManual" v-if="!manual">If your address isn't listed, you can
                    manually input it here</a>
                <a href="#" @click.prevent.stop="toggleManual" v-if="manual">Back to selecting your address from the list</a>
            </p>
        </div>

        <div v-show="manual">
            <p v-if="notFound">
                We haven't found any addresses for that postcode please enter your details manually.
            </p>

            <input-text
                input-type="text"
                :name="fieldNames.address1"
                v-model="address.address1"
                label="Address 1"
                placeholder="Address 1"
                :small="!large"
                :validation-state="validationState.address1"
            ></input-text>

            <input-text
                input-type="text"
                :name="fieldNames.address2"
                v-model="address.address2"
                label="Address 2"
                placeholder="Address 2"
                :small="!large"
                :validation-state="validationState.address2"
            ></input-text>

            <input-text
                input-type="text"
                :name="fieldNames.address3"
                v-model="address.address3"
                label="Address 3"
                placeholder="Address 3"
                :small="!large"
                :validation-state="validationState.address3"
            ></input-text>

            <input-text
                input-type="text"
                :name="fieldNames.town"
                v-model="address.town"
                label="Town"
                placeholder="Town"
                :small="!large"
                :validation-state="validationState.town"
            ></input-text>

            <input-text
                input-type="text"
                :name="fieldNames.county"
                v-model="address.county"
                label="County"
                placeholder="County"
                :small="!large"
                :validation-state="validationState.county"
            ></input-text>
        </div>
    </div>
</template>

<script lang="ts">
import {defineComponent, ref, watch} from 'vue';
import axiosClient from '../../modules/axiosClient';
import {CancelTokenSource} from 'axios';

type ApiStreet = {
    id: number,
    name: string,
    place: string,
}

export default defineComponent({
    props: {
        modelValue: {
            type: Object,
            default: () => {
                return {
                    postcode: '',
                    address1: '',
                    address2: '',
                    address3: '',
                    town: '',
                    county: '',
                };
            },
        },
        fieldNames: {
            type: Object,
            default: () => {
                return {
                    postcode: 'postcode',
                    address1: 'address1',
                    address2: 'address2',
                    address3: 'address3',
                    town: 'town',
                    county: 'county',
                };
            },
        },
        large: {
            type: Boolean,
            default: false,
        },
        email: {
            type: String,
            default: '',
        },
        validationState: {
            type: Object,
        },
    },
    emits: [
        'update:modelValue',
    ],
    data() {
        return {
            loadedPostcode: '',
            streets: [] as ApiStreet[],

            houseId: '',

            loadingStreet: false,
            loadingAddress: false,
            notFound: false,
            manual: false,
            timeoutHandler: null as any,
            cancelToken: null as CancelTokenSource|null,

            address: {
                postcode: '',
                address1: '',
                address2: '',
                address3: '',
                town: '',
                county: '',
            },
        };
    },
    watch: {
        modelValue: {
            handler(newValue, oldValue) {
                this.address.postcode = newValue.postcode;
                this.address.address1 = newValue.address1;
                this.address.address2 = newValue.address2;
                this.address.address3 = newValue.address3;
                this.address.town = newValue.town;
                this.address.county = newValue.county;

                if (newValue.postcode !== this.loadedPostcode) {
                    this.getStreets(true);
                }
            },
            deep: true,
        },
        address: {
            handler(newValue) {
                this.$emit('update:modelValue', newValue);
            },
            deep: true,
        },
        validationState: {
            handler(newValue) {
                if (newValue.$error) {
                    this.manual = true;
                }
            },
            deep: true,
        },
    },
    computed: {
        urlPostcode(): string {
            return encodeURIComponent(this.address.postcode.replace(/ /, ''));
        },
        disableLookup(): boolean {
            return this.loadedPostcode === this.address.postcode;
        },
        streetOptions(): DropdownOptions {
            const dropdownOptions = [];
            dropdownOptions.push({value: '', text: '-- choose a street --'});
            this.streets.forEach((street) => {
                dropdownOptions.push({value: street.id.toString(), text: street.name});
            });

            return dropdownOptions;
        },
    },
    methods: {
        getStreets(debounce: boolean) {
            if (this.timeoutHandler) {
                clearTimeout(this.timeoutHandler);
            }

            if (debounce) {
                this.timeoutHandler = setTimeout(()=> {
                    this.loadStreets();
                }, 500);
            } else {
                this.loadStreets();
            }
        },
        loadStreets() {
            if (this.address.postcode === '' || this.address.postcode === this.loadedPostcode) {
                return;
            }

            this.streets = [];
            this.houseId = '';

            if (this.cancelToken) {
                this.cancelToken.cancel();
            }
            this.cancelToken = axiosClient.CancelToken.source();
            this.loadingStreet = true;
            axiosClient.get('/api/v1/get-street/' + this.urlPostcode, {cancelToken: this.cancelToken.token}).then((response) => {
                this.loadedPostcode = this.modelValue.postcode;
                if (response.data.length > 0) {
                    this.streets = response.data as ApiStreet[];
                    this.manual = false;
                    this.notFound = false;
                } else {
                    this.notFound = true;
                    this.manual = true;
                }
            }, () => {
                this.notFound = true;
                this.manual = true;
            }).finally(() => {
                this.cancelToken = null;
                this.loadingStreet = false;
            });
        },
        getAddress() {
            if (this.houseId === '') {
                return;
            }

            this.loadingAddress = true;

            const payload = {
                'id': this.houseId,
                'email': this.email,
            } as any;

            axiosClient.post('/api/v1/get-street', payload).then((response) => {
                this.loadingAddress = false;
                if (response.data) {
                    this.address.address1 = response.data.address1;
                    this.address.address2 = response.data.address2;
                    this.address.address3 = response.data.address3;
                    this.address.town = response.data.town;
                    this.address.county = response.data.county;
                }
            }, () => {
                this.loadingAddress = false;
                this.notFound = true;
            });
        },

        toggleManual() {
            this.manual = !this.manual;
        },
    },
});
</script>
